import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import {
  buildVeriFactuRegistro,
  submitToVeriFactu,
  checkVeriFactuStatus,
} from '@/lib/invoicing/verifactu-service';

export default withAuth(async (req, res, user) => {
  if (req.method === 'POST') {
    return handleSubmit(req, res);
  }

  if (req.method === 'GET') {
    return handleStatusCheck(req, res);
  }

  return res.status(405).json({ error: 'Método no permitido' });
}, ['admin', 'asesor']);

async function handleSubmit(
  req: NextApiRequest,
  res: NextApiResponse
): Promise<void | NextApiResponse> {
  try {
    const { invoice_id } = req.body;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id es requerido' });
    }

    const { data: invoice, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('*')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !invoice) {
      return res.status(404).json({ error: 'Factura no encontrada' });
    }

    if (invoice.estado === 'borrador') {
      return res.status(400).json({
        error: 'La factura debe estar emitida antes de enviarla a VeriFactu',
      });
    }

    if (invoice.verifactu_enviada && invoice.verifactu_estado === 'aceptada') {
      return res.status(400).json({
        error: 'La factura ya ha sido aceptada por VeriFactu',
        verifactu_id: invoice.verifactu_id,
        verifactu_estado: invoice.verifactu_estado,
      });
    }

    const { data: lines } = await supabaseAdmin
      .from('invoice_lines')
      .select('*')
      .eq('invoice_id', invoice_id)
      .order('orden', { ascending: true });

    if (!lines || lines.length === 0) {
      return res.status(400).json({
        error: 'La factura debe tener líneas para enviarla a VeriFactu',
      });
    }

    const { data: serie } = await supabaseAdmin
      .from('invoice_series')
      .select('*')
      .eq('id', invoice.serie_id)
      .single();

    if (!serie?.verifactu_habilitado) {
      return res.status(400).json({
        error: 'La serie de esta factura no tiene VeriFactu habilitado',
      });
    }

    // Fetch empresa data for emisor NIF/nombre
    const { data: empresa } = await supabaseAdmin
      .from('empresas')
      .select('nif, nombre')
      .eq('id', invoice.empresa_id)
      .single();

    if (!empresa?.nif) {
      return res.status(400).json({
        error: 'La empresa no tiene NIF configurado. Requerido para VeriFactu.',
      });
    }

    const tipoComunicacion = invoice.verifactu_enviada ? 'A1' : 'A0';

    const registro = buildVeriFactuRegistro(
      invoice,
      lines,
      empresa.nif,
      empresa.nombre,
      tipoComunicacion as 'A0' | 'A1' | 'A4'
    );

    const result = await submitToVeriFactu(registro);

    const verifactuEstadoMap: Record<string, string> = {
      Correcto: 'aceptada',
      AceptadoConErrores: 'aceptada',
      Incorrecto: 'rechazada',
    };

    const updateData: Record<string, unknown> = {
      verifactu_enviada: true,
      verifactu_fecha_envio: new Date().toISOString(),
      verifactu_estado: verifactuEstadoMap[result.estado] || 'pendiente',
      updated_at: new Date().toISOString(),
    };

    if (result.csv) {
      updateData.verifactu_id = result.csv;
    }

    const { error: updateError } = await supabaseAdmin
      .from('invoices')
      .update(updateData)
      .eq('id', invoice_id);

    if (updateError) {
      console.error('[API verifactu] Error updating invoice after submission:', updateError);
      return res.status(500).json({
        error: 'Envío realizado pero error al actualizar la factura',
        verifactu_result: result,
      });
    }

    return res.status(200).json({
      success: true,
      estado: result.estado,
      csv: result.csv,
      verifactu_estado: verifactuEstadoMap[result.estado],
      errores: result.errores,
    });
  } catch (error: any) {
    console.error('[API verifactu] Unexpected error:', error);
    return res.status(500).json({
      error: error.message || 'Error interno del servidor',
    });
  }
}

async function handleStatusCheck(
  req: NextApiRequest,
  res: NextApiResponse
): Promise<void | NextApiResponse> {
  try {
    const invoice_id = req.query.invoice_id as string;

    if (!invoice_id) {
      return res.status(400).json({ error: 'invoice_id query param es requerido' });
    }

    const { data: invoice, error: fetchError } = await supabaseAdmin
      .from('invoices')
      .select('id, numero, verifactu_enviada, verifactu_id, verifactu_qr, verifactu_fecha_envio, verifactu_estado, empresa_id, fecha_emision')
      .eq('id', invoice_id)
      .single();

    if (fetchError || !invoice) {
      return res.status(404).json({ error: 'Factura no encontrada' });
    }

    if (!invoice.verifactu_enviada) {
      return res.status(200).json({
        success: true,
        verifactu_enviada: false,
        verifactu_estado: null,
        message: 'La factura no ha sido enviada a VeriFactu',
      });
    }

    const { data: empresa } = await supabaseAdmin
      .from('empresas')
      .select('nif')
      .eq('id', invoice.empresa_id)
      .single();

    if (empresa?.nif && invoice.numero && invoice.fecha_emision) {
      const statusResult = await checkVeriFactuStatus(
        empresa.nif,
        invoice.numero,
        invoice.fecha_emision
      );

      if (statusResult.success && statusResult.estado) {
        const verifactuEstadoMap: Record<string, string> = {
          Correcto: 'aceptada',
          AceptadoConErrores: 'aceptada',
          Incorrecto: 'rechazada',
        };
        const newEstado = verifactuEstadoMap[statusResult.estado];

        if (newEstado && newEstado !== invoice.verifactu_estado) {
          await supabaseAdmin
            .from('invoices')
            .update({
              verifactu_estado: newEstado,
              updated_at: new Date().toISOString(),
            })
            .eq('id', invoice_id);
        }
      }
    }

    return res.status(200).json({
      success: true,
      verifactu_enviada: invoice.verifactu_enviada,
      verifactu_id: invoice.verifactu_id,
      verifactu_qr: invoice.verifactu_qr,
      verifactu_fecha_envio: invoice.verifactu_fecha_envio,
      verifactu_estado: invoice.verifactu_estado,
    });
  } catch (error: any) {
    console.error('[API verifactu] Status check error:', error);
    return res.status(500).json({
      error: error.message || 'Error interno del servidor',
    });
  }
}
